import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { ReportProblemOutlined } from '@material-ui/icons';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiveWeightArea } from 'src/common/components/live-weight-area/live-weight-area';
import { IdmInvalidUserNamePasswordLoginProvided } from 'src/common/helpers/error-code';
import XLRLogger from 'src/common/helpers/logger';
import {
  ForgotPasswordActivityTypes,
  IForgotPassword,
} from 'src/common/interfaces/dtos/IForgotPassword';
import { IForgotPasswordErrors } from 'src/common/interfaces/dtos/IForgotPasswordErrors';
import { ILoginUser } from 'src/common/interfaces/dtos/ILoginUser';
import { store } from 'src/common/redux/configure-store';
import { ResetPasswordActions } from 'src/common/redux/reducers/reset-password-reducer';
import { newStyles } from 'src/touchpoint/components/login-emerald-styles';
import { idmForgotPassword } from './business-logic/service/idm-forgot-password';
import ResetPasswordForm from './components/reset-password-form';
import ValidateSupervisorCredentialsForm from './components/validate-supervisor-credentials-form';

const ResetPassword: React.FC<IResetPasswordProps> = (props: IResetPasswordProps): JSX.Element => {
  const classes = newStyles();
  const [supervisorValidated, setSupervisorValidated] = useState<boolean>(false);
  const [supervisorDetails, setSupervisorDetails] = useState<ILoginUser>({});
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [errors, setErrors] = useState<IForgotPasswordErrors>({});
  const { t } = useTranslation();
  const { setUser, user } = props;

  const verifySupervisorCredentials = useCallback(
    async (forgotPassword: IForgotPassword): Promise<void> => {
      await idmForgotPassword(forgotPassword)
        .then((): void => {
          setSupervisorValidated(true);
          setErrors({});
          XLRLogger.debug({
            message: `Validate Supervisor: ${supervisorDetails.userName}`,
            source: 'forgot-password.tsx',
          });
        })
        .catch((err): void => {
          errors.GeneralError =
            err.code === IdmInvalidUserNamePasswordLoginProvided
              ? t('IdmInvalidUserNamePasswordLoginProvided_Reset_Password')
              : err.message;
          setErrors({
            ...errors,
          });
        });
    },
    [t, errors, supervisorDetails],
  );

  const resetUserPassword = useCallback(
    async (forgotPassword: IForgotPassword, logUserIn): Promise<void> => {
      await idmForgotPassword(forgotPassword)
        .then(
          async (): Promise<void> => {
            XLRLogger.debug({
              message: `reset username: ${user.userName}`,
              source: 'forgot-password.tsx',
            });
            await logUserIn();
            store.dispatch(ResetPasswordActions.setResetPassword(false));
            store.dispatch(ResetPasswordActions.showResetSuccessModal(true));
          },
        )
        .catch((err): void => {
          errors.GeneralError = err.message;
          setErrors({
            ...errors,
          });
        });
    },
    [errors, user],
  );

  const cancelResetButton = {
    label: t('Cancel_Button'),
    disabled: false,
    onClick: () => {
      store.dispatch(ResetPasswordActions.cancelReset());
    },
  };

  const submitValidateSupervisorCredentialsForm = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const activity = ForgotPasswordActivityTypes.ValidateSupervisorCredentials;
    const userDetails = user;
    await verifySupervisorCredentials({ supervisorDetails, userDetails, activity });
  };

  const submitResetPasswordForm = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const activity = ForgotPasswordActivityTypes.AuthorizedChangePassword;
    const userDetails = user;
    await resetUserPassword({ supervisorDetails, userDetails, activity }, () =>
      props.onSubmit(event),
    );
  };

  return (
    <Grid container item sm={7}>
      <Grid item container sm={8} className={classes.topIconPanel} />
      <Grid item container sm={4} justifyContent={'center'} className={classes.topIconPanel}>
        <LiveWeightArea isLeftHanded={false} isSummaryDisabled={false} isReversed={true} />
      </Grid>
      <Grid container item sm={2} />
      <Grid container item sm={8} className={classes.registerContainer}>
        <Grid container justifyContent='space-between' alignItems='center'>
          <Grid item>
            <Typography
              className={classes.typographytitle}
              display='inline'
              data-testid={'reset-password-message'}
            >
              {t('ForgotPasswordSupervisorConfirmationCaption')}
            </Typography>
            <Grid item xs={1}>
              <Typography className={classes.typographytitlecontainer} />
            </Grid>
          </Grid>
        </Grid>
        <Grid container className={classes.formError}>
          {errors.GeneralError && (
            <Grid item container>
              <ReportProblemOutlined classes={{ root: `${classes.erroricon}` }} />
              <span className={classes.errorMessage} data-testid='FormErrorLabel'>
                {errors.GeneralError}
              </span>
            </Grid>
          )}
        </Grid>
        {!supervisorValidated ? (
          <ValidateSupervisorCredentialsForm
            cancelButton={cancelResetButton}
            onSubmit={submitValidateSupervisorCredentialsForm}
            supervisorDetails={supervisorDetails}
            userDetails={user}
            setSupervisorDetails={setSupervisorDetails}
            setUserDetails={setUser}
            errors={errors}
            setErrors={setErrors}
          />
        ) : (
          <ResetPasswordForm
            cancelButton={cancelResetButton}
            onSubmit={submitResetPasswordForm}
            setUserDetails={setUser}
            userDetails={user}
            passwordConfirm={passwordConfirm}
            setPasswordConfirm={setPasswordConfirm}
            errors={errors}
            setErrors={setErrors}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default ResetPassword;

export interface IResetPasswordProps {
  user: ILoginUser;
  setUser: (state: ILoginUser) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}
